import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { Briefcase, Edit3, ExternalLink, Eye, Lock, RefreshCw } from "lucide-react";
import Layout from "../Layout/Layout";
import { isAuthenticated } from "../auth";
import { getMyJobApplications, getJobApplicationById } from "../auth/apiJobs";
import ButtonPagination from "../pageInside/ButtonPagination";
import JobApplicationView from "../pageInside/jobs/JobApplicationView";
import JobApplicationForm from "../pageInside/jobs/JobApplicationForm";
import { elakayAutoText } from "../pageInside/dataElakay";
import "./jobDashboard.css";
import "./myJobApplications.css";

const STATUS_LABELS = {
  submitted: "Soumise",
  reviewing: "En revue",
  shortlisted: "Preselectionnee",
  interview: "Entretien",
  offered: "Offre envoyee",
  rejected: "Rejetee",
  withdrawn: "Retiree",
  approved: "Approuvee",
};

// Mirrors EDITABLE_APPLICATION_STATUSES in server_elakay/elakay_models/job.js
// so the Edit button can be disabled/labeled instantly without a round trip.
// The backend remains the sole authority: getJobApplicationById's `canEdit`
// flag and updateMyJobApplication's own check are what actually gate the
// save -- this is only a UI hint (defense-in-depth stays server-side).
const EDITABLE_APPLICATION_STATUSES = ["submitted", "reviewing"];
const canEditRow = (application) => EDITABLE_APPLICATION_STATUSES.includes(application?.status);

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const MyJobApplications = ({ infoSource }) => {
  const auth = isAuthenticated();
  const [applications, setApplications] = useState([]);
  const [count, setCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const limit = 20;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [viewingApplication, setViewingApplication] = useState(null);
  const [editState, setEditState] = useState({ loading: false, error: "", application: null, canEdit: false });

  const loadApplications = async (nextSkip = 0) => {
    setLoading(true);
    setError("");
    try {
      const data = await getMyJobApplications({ skip: nextSkip, limit }, auth?.token);
      setApplications(data.applications || []);
      setCount(data.count || 0);
      setSkip(data.skip || 0);
    } catch (err) {
      setError(err.message || elakayAutoText("h_myapps_load_error", "Impossible de charger tes candidatures."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApplications(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = () => loadApplications(skip + limit);
  const loadMore0 = () => loadApplications(Math.max(skip - limit, 0));

  const openView = (application) => setViewingApplication(application);
  const closeView = () => setViewingApplication(null);

  const openEdit = async (application) => {
    if (!canEditRow(application)) return;
    setEditState({ loading: true, error: "", application: null, canEdit: false });
    try {
      const data = await getJobApplicationById(application._id, auth?.token);
      setEditState({
        loading: false,
        error: "",
        application: data.application,
        canEdit: Boolean(data.canEdit),
      });
    } catch (err) {
      setEditState({
        loading: false,
        error: err.message || elakayAutoText("h_myapps_edit_load_error", "Impossible de charger cette candidature."),
        application: null,
        canEdit: false,
      });
    }
  };
  const closeEdit = () => setEditState({ loading: false, error: "", application: null, canEdit: false });

  // Reflect the saved changes immediately in the table (no page refresh
  // needed to "see the updated information", per spec) and keep the modal's
  // own data in sync so its inline success message stays accurate.
  const handleEditSuccess = (updatedApplication) => {
    if (!updatedApplication?._id) return;
    setApplications((prev) =>
      prev.map((item) => (item._id === updatedApplication._id ? { ...item, ...updatedApplication } : item))
    );
    setEditState((prev) => ({ ...prev, application: { ...prev.application, ...updatedApplication } }));
  };

  const editModalOpen = editState.loading || editState.application || editState.error;

  return (
    <Layout
      infoSource={infoSource}
      loading={false}
      SliderPunShow={false}
      outOffPage={false}
      showFilterMenu={false}
      enableBodyParticles={false}
      enableFloatingSquares={false}
    >
      <main className="my-apps-page" style={infoSource?.styleNav}>
        <div className="my-apps-shell">
          <header className="my-apps-header">
            <div>
              <h1>{elakayAutoText("h_myapps_title", "Mes candidatures")}</h1>
              <p>{elakayAutoText("h_myapps_subtitle", "Suis le statut des postes auxquels tu as postule chez Elakay.")}</p>
            </div>
            <button className="my-apps-refresh" type="button" onClick={() => loadApplications(skip)}>
              <RefreshCw size={16} />
              {elakayAutoText("h_myapps_refresh", "Actualiser")}
            </button>
          </header>

          {error && <div className="my-apps-message error">{error}</div>}

          {loading && <div className="my-apps-empty">{elakayAutoText("h_myapps_loading", "Chargement...")}</div>}

          {!loading && applications.length === 0 && !error && (
            <div className="my-apps-empty">
              <Briefcase size={28} />
              <h2>{elakayAutoText("h_myapps_empty_title", "Aucune candidature pour le moment.")}</h2>
              <p>{elakayAutoText("h_myapps_empty_body", "Parcours les postes ouverts et postule pour les voir apparaitre ici.")}</p>
              <Link className="my-apps-link" to="/jobs">
                {elakayAutoText("h_myapps_browse", "Voir les postes ouverts")}
              </Link>
            </div>
          )}

          {!loading && applications.length > 0 && (
            <table className="my-apps-table">
              <thead>
                <tr>
                  <th>{elakayAutoText("h_myapps_col_position", "Poste")}</th>
                  <th>{elakayAutoText("h_myapps_col_department", "Departement")}</th>
                  <th>{elakayAutoText("h_myapps_col_status", "Statut")}</th>
                  <th>{elakayAutoText("h_myapps_col_date", "Date de candidature")}</th>
                  <th>{elakayAutoText("h_myapps_col_resume", "CV")}</th>
                  <th>{elakayAutoText("h_myapps_col_actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((application) => {
                  const job = application.jobId || {};
                  const editable = canEditRow(application);
                  return (
                    <tr key={application._id}>
                      <td data-label={elakayAutoText("h_myapps_col_position", "Poste")}>
                        {job.slug ? (
                          <Link to={`/jobs/${job.slug}`}>{job.title || elakayAutoText("h_myapps_unknown_job", "Poste")}</Link>
                        ) : (
                          job.title || elakayAutoText("h_myapps_unknown_job", "Poste")
                        )}
                      </td>
                      <td data-label={elakayAutoText("h_myapps_col_department", "Departement")}>{job.department || "Elakay"}</td>
                      <td data-label={elakayAutoText("h_myapps_col_status", "Statut")}>
                        <span className={`my-apps-pill my-apps-pill--${application.status}`}>
                          {STATUS_LABELS[application.status] || application.status}
                        </span>
                      </td>
                      <td data-label={elakayAutoText("h_myapps_col_date", "Date de candidature")}>{formatDate(application.createdAt)}</td>
                      <td data-label={elakayAutoText("h_myapps_col_resume", "CV")}>
                        {application.resumeUrl ? (
                          <a href={application.resumeUrl} target="_blank" rel="noopener noreferrer">
                            <ExternalLink size={14} />
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td data-label={elakayAutoText("h_myapps_col_actions", "Actions")} className="my-apps-actions">
                        <button type="button" className="my-apps-btn my-apps-btn--view" onClick={() => openView(application)}>
                          <Eye size={14} />
                          {elakayAutoText("h_myapps_view", "Voir")}
                        </button>
                        <button
                          type="button"
                          className="my-apps-btn my-apps-btn--edit"
                          onClick={() => openEdit(application)}
                          disabled={!editable}
                          title={
                            editable
                              ? undefined
                              : elakayAutoText(
                                  "h_myapps_locked",
                                  "Cette candidature ne peut plus etre modifiee a cette etape du recrutement."
                                )
                          }
                        >
                          {editable ? <Edit3 size={14} /> : <Lock size={14} />}
                          {elakayAutoText("h_myapps_edit", "Modifier")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {!loading && count > 0 && (
            <ButtonPagination
              infoSource={infoSource}
              size={applications.length}
              siseProduct={count}
              skip={skip}
              limit={limit}
              loadMore={loadMore}
              loadMore0={loadMore0}
            />
          )}
        </div>
      </main>

      {viewingApplication && (
        <Modal show size="lg" centered onHide={closeView} dialogClassName="job-admin-modal">
          <button type="button" className="job-admin-modal-close" onClick={closeView} aria-label={elakayAutoText("h_bbfa773e5a63a5", "Close")}>×</button>
          <Modal.Body>
            <JobApplicationView application={viewingApplication} />
          </Modal.Body>
        </Modal>
      )}

      {editModalOpen && (
        <Modal show size="lg" centered onHide={closeEdit} dialogClassName="job-admin-modal">
          <button type="button" className="job-admin-modal-close" onClick={closeEdit} aria-label={elakayAutoText("h_bbfa773e5a63a5", "Close")}>×</button>
          <Modal.Body>
            {editState.loading && <div className="my-apps-empty">{elakayAutoText("h_myapps_loading", "Chargement...")}</div>}
            {!editState.loading && editState.error && <div className="my-apps-message error">{editState.error}</div>}
            {!editState.loading && editState.application && !editState.canEdit && (
              <div className="my-apps-message error">
                {elakayAutoText("h_myapps_locked", "Cette candidature ne peut plus etre modifiee a cette etape du recrutement.")}
              </div>
            )}
            {!editState.loading && editState.application && editState.canEdit && (
              <>
                <h2>{elakayAutoText("h_myapps_edit_title", "Modifier ma candidature")}</h2>
                <JobApplicationForm
                  job={editState.application.jobId}
                  mode="edit"
                  application={editState.application}
                  onSuccess={handleEditSuccess}
                />
              </>
            )}
          </Modal.Body>
        </Modal>
      )}
    </Layout>
  );
};

export default MyJobApplications;
